import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v || 0);

const STATUS_CFG = {
  open: { label: 'Em andamento', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  won:  { label: 'Ganho',        cls: 'bg-green-100 text-green-800 border-green-200' },
  lost: { label: 'Perdido',      cls: 'bg-red-100 text-red-800 border-red-200' },
};

const ACT_CFG = {
  call:    { icon: '📞', label: 'Ligação' },
  email:   { icon: '📧', label: 'E-mail' },
  meeting: { icon: '🤝', label: 'Reunião' },
  task:    { icon: '✅', label: 'Tarefa' },
  note:    { icon: '📝', label: 'Anotação' },
};

const QUOTE_CFG = {
  draft:     { label: 'Rascunho',  cls: 'bg-gray-100 text-gray-600' },
  sent:      { label: 'Enviada',   cls: 'bg-blue-100 text-blue-700' },
  approved:  { label: 'Aprovada',  cls: 'bg-green-100 text-green-700' },
  rejected:  { label: 'Rejeitada', cls: 'bg-red-100 text-red-700' },
  converted: { label: 'Convertida',cls: 'bg-purple-100 text-purple-700' },
};

const PROPOSAL_CFG = {
  draft:    { label: 'Rascunho',  cls: 'bg-gray-100 text-gray-600' },
  sent:     { label: 'Enviada',   cls: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Aceita',    cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejeitada', cls: 'bg-red-100 text-red-700' },
  expired:  { label: 'Expirada',  cls: 'bg-amber-100 text-amber-700' },
};

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '—'; }
};

const fmtDateTime = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return '—'; }
};

const fmtDays = (d) => {
  if (!d) return '—';
  try {
    const diff = Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));
    return `${diff}d`;
  } catch { return '—'; }
};

export default function NegotiationsPanel({ api, users: usersProp = [], entityType = 'company', entityId, emptyHint }) {
  const [users, setUsers] = useState(usersProp);
  const [data, setData] = useState({ items: [], totals: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [responsibleFilter, setResponsibleFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at:desc');

  // Load users list for the responsible filter when not passed by the parent
  useEffect(() => {
    if (usersProp && usersProp.length > 0) return;
    api.get('/users').then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [api, usersProp]);

  const baseUrl = entityType === 'lead' ? `/leads/${entityId}/negotiations` : `/companies/${entityId}/negotiations`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (stageFilter) params.set('stage', stageFilter);
      if (responsibleFilter) params.set('responsible_id', responsibleFilter);
      const [sort_field, sort_order] = sortBy.split(':');
      params.set('sort_by', sort_field);
      params.set('sort_order', sort_order);
      const res = await api.get(`${baseUrl}?${params.toString()}`);
      setData(res.data || { items: [], totals: {} });
    } catch {
      toast.error('Erro ao carregar negociações');
      setData({ items: [], totals: {} });
    } finally {
      setLoading(false);
    }
  }, [api, baseUrl, search, statusFilter, stageFilter, responsibleFilter, sortBy]);

  useEffect(() => { load(); }, [load]);

  const hasFilter = () => !!search.trim() || !!statusFilter || !!stageFilter || !!responsibleFilter;
  const clearFilters = () => {
    setSearch(''); setStatusFilter(''); setStageFilter(''); setResponsibleFilter(''); setSortBy('created_at:desc');
  };

  // Distinct stages derived from loaded negotiations
  const stages = [...new Set((data.items || []).map(d => d.stage).filter(Boolean))].sort();

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const totals = data.totals || {};

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-gray-900">{totals.total ?? 0}</div>
          <div className="text-xs text-gray-500">Negociações</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-blue-700">{totals.open_count ?? 0}</div>
          <div className="text-xs text-blue-600">{fmt(totals.open_value)} em andamento</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-green-700">{totals.won_count ?? 0}</div>
          <div className="text-xs text-green-600">{fmt(totals.won_value)} ganhos</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-red-700">{totals.lost_count ?? 0}</div>
          <div className="text-xs text-red-600">perdidas</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-2xl font-bold text-amber-700">{totals.overdue_count ?? 0}</div>
          <div className="text-xs text-amber-600">em atraso</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          className="w-56"
          placeholder="Buscar negociação..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Select value={statusFilter || '__all__'} onValueChange={v => setStatusFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os status</SelectItem>
            <SelectItem value="open">Em andamento</SelectItem>
            <SelectItem value="won">Ganhas</SelectItem>
            <SelectItem value="lost">Perdidas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stageFilter || '__all__'} onValueChange={v => setStageFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Estágio" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os estágios</SelectItem>
            {stages.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={responsibleFilter || '__all__'} onValueChange={v => setResponsibleFilter(v === '__all__' ? '' : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Responsável" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os responsáveis</SelectItem>
            {(users || []).map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-52"><SelectValue placeholder="Ordenar por" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_at:desc">Mais recentes</SelectItem>
            <SelectItem value="value:desc">Maior valor</SelectItem>
            <SelectItem value="value:asc">Menor valor</SelectItem>
            <SelectItem value="expected_close_date:asc">Fechamento previsto</SelectItem>
            <SelectItem value="last_activity_date:desc">Última atividade</SelectItem>
          </SelectContent>
        </Select>
        {hasFilter() && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>Limpar</Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-28 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : (data.items || []).length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <div className="text-5xl mb-3">🤝</div>
          <p className="font-semibold text-gray-700">Nenhuma negociação encontrada</p>
          <p className="text-sm text-gray-500 mt-1">
            {hasFilter() ? 'Ajuste os filtros para ver mais resultados.' : (emptyHint || 'As negociações (negócios) aparecerão aqui com seus estágios.')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {(data.items || []).map(deal => {
            const sc = STATUS_CFG[deal.status] || STATUS_CFG.open;
            const isOpen = expanded.has(deal.id);
            return (
              <div key={deal.id} className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                {/* Card header */}
                <button
                  onClick={() => toggleExpand(deal.id)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-gray-900 ${isOpen ? 'text-blue-700' : ''}`}>{deal.title}</span>
                    <Badge variant="outline" className="text-xs bg-gray-50">{deal.stage}</Badge>
                    <Badge variant="outline" className={`text-xs border ${sc.cls}`}>{sc.label}</Badge>
                    {deal.is_overdue && (
                      <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">⚠ Atrasada</Badge>
                    )}
                    <span className="ml-auto text-sm font-semibold text-gray-900">{fmt(deal.value)}</span>
                    <span className={`text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`}>▾</span>
                  </div>
                </button>

                {/* Card body summary */}
                <div className="px-4 pb-3 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-xs text-gray-600">
                  <div><span className="text-gray-400">Probabilidade:</span> <span className="font-medium">{deal.probability}%</span></div>
                  <div><span className="text-gray-400">Responsável:</span> <span className="font-medium">{deal.owner_name || '—'}</span></div>
                  <div><span className="text-gray-400">Prazo:</span> <span className="font-medium">{fmtDate(deal.expected_close_date)}</span></div>
                  <div><span className="text-gray-400">No estágio:</span> <span className="font-medium">{deal.days_in_stage != null ? `${deal.days_in_stage}d` : '—'}</span></div>
                  <div><span className="text-gray-400">Próx. atividade:</span> <span className="font-medium">{deal.next_activity_type ? `${ACT_CFG[deal.next_activity_type]?.icon || ''} ${fmtDate(deal.next_activity_date)}` : '—'}</span></div>
                  <div><span className="text-gray-400">Última atividade:</span> <span className="font-medium">{deal.last_activity_date ? `${fmtDays(deal.last_activity_date)}` : '—'}</span></div>
                  <div><span className="text-gray-400">Contadores:</span> <span className="font-medium">📅 {deal.activities_count || 0} · 💼 {deal.quotes_count || 0} · 📄 {deal.proposals_count || 0} · 📁 {deal.documents_count || 0}</span></div>
                  <div><span className="text-gray-400">Cliente:</span> <span className="font-medium truncate">{deal.person_name || '—'}</span></div>
                </div>

                {/* Expanded detail */}
                {isOpen && (
                  <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-gray-600">
                      <div className="bg-white rounded-lg border border-gray-100 p-2"><span className="text-gray-400 block">Criado em</span><span className="font-medium">{fmtDateTime(deal.created_at)}</span></div>
                      <div className="bg-white rounded-lg border border-gray-100 p-2"><span className="text-gray-400 block">Estágio desde</span><span className="font-medium">{fmtDateTime(deal.stage_entered_at)}</span></div>
                      <div className="bg-white rounded-lg border border-gray-100 p-2"><span className="text-gray-400 block">E-mail contato</span><span className="font-medium break-all">{deal.person_email || '—'}</span></div>
                      <div className="bg-white rounded-lg border border-gray-100 p-2"><span className="text-gray-400 block">Empresa</span><span className="font-medium">{deal.organization_name || '—'}</span></div>
                    </div>

                    {/* Recent activities */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">📅 Atividades recentes (últimas {Math.min((deal.recent_activities || []).length, 5)})</p>
                      {(deal.recent_activities || []).length === 0 ? (
                        <p className="text-xs text-gray-400">Sem atividades registradas</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(deal.recent_activities || []).map(a => (
                            <div key={a.id || a.subject + a.created_at} className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-3 py-1.5 text-xs">
                              <span>{ACT_CFG[a.activity_type]?.icon || '📌'}</span>
                              <span className="flex-1 font-medium">{a.subject}</span>
                              {a.completed ? <Badge className="text-[10px] bg-green-100 text-green-800">Concluída</Badge> : <Badge className="text-[10px] bg-yellow-100 text-yellow-800">Pendente</Badge>}
                              <span className="text-gray-400 shrink-0">{fmtDateTime(a.created_at)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quotes */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">💼 Cotações ({deal.quotes_count || 0})</h4>
                      {(deal.quotes || []).length === 0 ? (
                        <p className="text-xs text-gray-400">Nenhuma cotação</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(deal.quotes || []).map(q => {
                            const qc = QUOTE_CFG[q.status] || QUOTE_CFG.draft;
                            return (
                              <div key={q.id} className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-3 py-1.5 text-xs">
                                <span className="flex-1 font-medium">{q.quote_number || '—'} · {q.title}</span>
                                <span className="text-gray-600">{fmt(q.total_amount)}</span>
                                <span className={`px-2 py-0.5 rounded-full ${qc.cls}`}>{qc.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Proposals */}
                    <div>
                      <h4 className="text-xs font-semibold text-gray-700 mb-2">📄 Propostas ({deal.proposals_count || 0})</h4>
                      {(deal.proposals || []).length === 0 ? (
                        <p className="text-xs text-gray-400">Nenhuma proposta</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(deal.proposals || []).map(p => {
                            const pc = PROPOSAL_CFG[p.status] || PROPOSAL_CFG.draft;
                            return (
                              <div key={p.id} className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-3 py-1.5 text-xs">
                                <span className="flex-1 font-medium">{p.proposal_number || '—'} · {p.title}</span>
                                <span className="text-gray-600">{fmt(p.total_amount)}</span>
                                <span className={`px-2 py-0.5 rounded-full ${pc.cls}`}>{pc.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
